import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { provisionCurrentUser } from '@/utils/provisionUser';
import { logger } from '@equip/utils/logger';

// Minimum gap between heartbeat pings to avoid flooding audit_logs.
const PING_MIN_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fires POST /api/rest/app/v1/users/me (provision_me) on client-side navigation while
 * the user is authenticated, at most once every 5 minutes.
 *
 * This records an ACTIVITY event in audit_logs so that the "Last Activity"
 * column on the User Management screen reflects actual usage.
 *
 * Must be called from a component inside the React Router context
 * (e.g. MainLayout) so that useLocation is available.
 */
const ping = (): void => {
  provisionCurrentUser().catch((err: unknown) => {
    logger.warn('Activity heartbeat failed', {
      component: 'useActivityHeartbeat',
      error: String(err),
    });
  });
};

export const useActivityHeartbeat = (): void => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  const lastPingAt = useRef<number>(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    const now = Date.now();
    if (now - lastPingAt.current < PING_MIN_INTERVAL_MS) return;
    lastPingAt.current = now;
    ping();
  }, [isAuthenticated, location.pathname]);
};
